import client from "FrontendJsAssets/common/client";
import { Vue, i18n } from "Common/vue";
import Vuex from "vuex";
import { initTippy, tippyAlert } from "Functions/tippy";

Vue.use(Vuex);

const endpoints = {
    currentReservation: "/aktualna-rezervacia",
    addProductToReservation: "/pridat-produkt-rezervacia",
    removeProductFromReservation: "/odstranit-produkt-rezervacia",
    submitReservation: "/odoslat-rezervaciu",
    addVoucher: "/pridat-voucher",
    removeVoucher: "/odstranit-voucher",
    changeQuantity: "/zmenit-mnozstvo-rezervacia",
    removeReservation: "/odstranit-rezervaciu",
    changeBranch: "/zmenit-pobocku-rezervacie",
    returnUnwantedProduct: "/vratit-neziadany-produkt",
};

const BranchReservationStore = new Vuex.Store({
    state: {
        hasPendingRequest: false,
        reservationData: null,
        successReservationData: null,
        productChanges: null,
    },
    getters: {
        // TODO: probably some better way, than just checking for length of products
        isEmpty(state) {
            const hasProducts = state.reservationData?.products?.length > 0;
            const modalEl = document.querySelector(".js-product-reservation-modal");

            if (modalEl) {
                modalEl.classList.toggle("is-empty", !hasProducts);
            }

            return !hasProducts;
        },
        isSuccess(state) {
            const isReservationSuccess = state.successReservationData !== null;
            const modalEl = document.querySelector(".js-product-reservation-modal");

            if (modalEl) {
                modalEl.classList.toggle("is-success", isReservationSuccess);
            }

            return isReservationSuccess;
        },
        hasVouchers(state) {
            return state.reservationData?.vouchers?.length > 0;
        },
        reservationCutoffDate(state) {
            return state.reservationData?.branch?.custom_reservation_cutoff_with_year;
        },
    },
    actions: {
        async doRequest({ commit, dispatch, state }, requestConfig = {}) {
            if (state.hasPendingRequest) {
                return;
            }

            commit("setHasPendingRequest", true);

            let response = {};
            try {
                response = await client.request(requestConfig);
            } catch (error) {
                if (error?.response?.data?.message) {
                    console.error("SERVER_ERROR", error.response.data.message);
                } else {
                    console.error(error);
                }

                dispatch("showAlert", {
                    message: i18n.t("BranchReservation.serverErrorMessage"),
                    tippyOptions: {
                        theme: "error",
                    },
                    delay: 10000,
                });
            }

            if (response?.data?.reservationData) {
                commit("setReservationData", response.data.reservationData);
            }

            commit("setHasPendingRequest", false);

            if (requestConfig?.data?.changeBranch) {
                const { data: responseData } = await dispatch("selectBranch", {
                    branchId: state.reservationData.branch.id,
                });

                const additionalData = responseData?.additionalData;

                if (!responseData?.status && additionalData?.productChanges?.length) {
                    commit("setProductChanges", additionalData.productChanges);

                    setTimeout(() => {
                        window.Eshop.confirm(
                            responseData.message,
                            {
                                allowOutsideClick: false,
                                icon: false,
                                html: '<div class="js-branch-select-popup-target"></div>',
                                confirmButtonText: "Ok",
                                showDenyButton: false,
                                willOpen: (swalEl) => {
                                    const popupContentHtml = document.querySelector(
                                        ".js-branch-select-popup-content"
                                    ).innerHTML;
                                    swalEl.querySelector(
                                        ".js-branch-select-popup-target"
                                    ).innerHTML = popupContentHtml;
                                },
                            },
                            // on confirm
                            async () => {
                                const { data: forcedResponseData } = await dispatch(
                                    "selectBranch",
                                    {
                                        branchId: state.reservationData.branch.id,
                                        force: true,
                                    }
                                );

                                dispatch("showAlert", {
                                    targetEl: document.querySelector(
                                        ".c-reservation-branch-select__button"
                                    ),
                                    message: forcedResponseData.message,
                                    tippyOptions: {
                                        theme: forcedResponseData.status ? "success" : "error",
                                    },
                                    delay: forcedResponseData.status ? 4000 : 10000,
                                });
                            },
                            // always
                            () => {
                                // state.productChanges = null;
                                commit("setProductChanges", null);
                            }
                        );
                    }, 250);
                }
            }

            return response;
        },
        async addProductToReservation({ dispatch }, { productId }) {
            return await dispatch("doRequest", {
                method: "POST",
                url: endpoints.addProductToReservation,
                data: {
                    product_id: productId,
                },
            });
        },
        async removeProductFromReservation({ dispatch }, { productId }) {
            return await dispatch("doRequest", {
                method: "POST",
                url: endpoints.removeProductFromReservation,
                data: {
                    product_id: productId,
                },
            });
        },
        async changeProductQuantity({ dispatch }, { productId, quantity }) {
            return await dispatch("doRequest", {
                method: "POST",
                url: endpoints.changeQuantity,
                data: {
                    product_id: productId,
                    quantity: quantity,
                },
            });
        },
        async retrieveCurrentReservation({ getters, dispatch }) {
            if (getters.isSuccess) return;

            return await dispatch("doRequest", {
                method: "GET",
                url: endpoints.currentReservation,
            });
        },
        async selectBranch({ dispatch }, { branchId, force = false }) {
            return await dispatch("doRequest", {
                method: "POST",
                url: endpoints.changeBranch,
                data: {
                    branch_id: branchId,
                    force: force,
                },
            });
        },
        async addVoucher({ dispatch }, { voucherCode }) {
            return await dispatch("doRequest", {
                method: "POST",
                url: endpoints.addVoucher,
                data: {
                    voucher_code: voucherCode,
                },
            });
        },
        async removeVoucher({ dispatch }, voucherCartId) {
            return await dispatch("doRequest", {
                method: "POST",
                url: endpoints.removeVoucher,
                data: {
                    voucher_id: voucherCartId,
                },
            });
        },
        async submitReservation({ dispatch }, { email, phone }) {
            return await dispatch("doRequest", {
                method: "POST",
                url: endpoints.submitReservation,
                data: {
                    email: email,
                    phone: phone,
                },
            });
        },
        async cancelReservation({ dispatch }) {
            return await dispatch("doRequest", {
                url: endpoints.removeReservation,
            });
        },
        async returnUnwantedProduct({ dispatch }, { productId }) {
            return await dispatch("doRequest", {
                method: "POST",
                url: endpoints.returnUnwantedProduct,
                data: {
                    product_id: productId,
                    refresh: true,
                    changeBranch: true,
                },
            });
        },
        setSuccessReservation({ commit }, successReservationData) {
            return commit("setSuccessReservation", successReservationData);
        },
        hideReservationModalAndShowMessage({ dispatch }, { message, tippyOptions = {} }) {
            Eshop.alert(message, {
                type: "success",
                delay: 10000,
            });

            $("#product-reservation").modal("hide");
        },
        showAlert({}, { targetEl, message, tippyOptions = {}, delay }) {
            if (!targetEl) {
                targetEl = document.querySelector(".js-reservation-tippy-target");
            }

            tippyAlert(
                targetEl,
                message,
                {
                    ...{
                        theme: "success",
                        zIndex: 9999,
                    },
                    ...tippyOptions,
                },
                delay
            );
        },
    },
    mutations: {
        setProductChanges(state, productChanges) {
            state.productChanges = productChanges;
        },
        setReservationData(state, reservationData) {
            // TODO: temp fix, from BE it's coming as an object, therefore conversion to array
            reservationData.unwantedProducts = Object.values(reservationData.unwantedProducts);
            reservationData.products = Object.values(reservationData.products);
            reservationData.vouchers = Object.values(reservationData.vouchers);
            reservationData.availableBranches = Object.values(reservationData.availableBranches);

            state.reservationData = reservationData;
            state.successReservationData = null;

            const unwantedIds = new Set(reservationData.unwantedProducts.map((p) => p.id));
            reservationData.products = reservationData.products.filter(
                (p) => !unwantedIds.has(p.product_id)
            );

            toggleDisabledOnReservationTriggers(reservationData.branch?.id);
            updateCountsInButtons(reservationData);
        },
        setHasPendingRequest(state, hasPendingRequest) {
            state.hasPendingRequest = hasPendingRequest;
        },
        setSuccessReservation(state, successReservationData) {
            state.successReservationData = successReservationData;

            localStorage.removeItem("reservation-email");
            localStorage.removeItem("reservation-phone");

            toggleDisabledOnReservationTriggers();
        },
    },
});

function toggleDisabledOnReservationTriggers(selectedBranchId = null) {
    for (const addToReservationEl of document.querySelectorAll(".js-add-to-reservation")) {
        const branchesAvailability =
            JSON.parse(addToReservationEl.dataset.branchesAvailability) || [];
        const isDisabled = selectedBranchId && !branchesAvailability.includes(selectedBranchId);

        addToReservationEl.classList.toggle("is-disabled", isDisabled);
        addToReservationEl.classList.toggle("js-tippy", isDisabled);

        if (!isDisabled && addToReservationEl._tippy) {
            addToReservationEl._tippy.destroy();
        }

        if (isDisabled && !addToReservationEl._tippy) {
            initTippy();
        }
    }
}

function updateCountsInButtons(reservationData) {
    for (const reservationCountEl of document.querySelectorAll(".js-reservation-count")) {
        const productsCount = reservationData?.products?.length || 0;

        reservationCountEl.innerText = productsCount;
        reservationCountEl.dataset.count = productsCount;
    }
}

export default BranchReservationStore;
